import i18n from '@/i18n';
import { getIpeUiLocale } from '@/i18n/storage';
import { formatCreditAmount } from '@/constants/ipeCredits';
import { normalizeWorkspaceMode, resolveScopedQuota } from './accountCenterUtils';
import {
  isUserStorageCapacityWarning,
  resolveUserStorageCapacityWarningMessage,
} from './openStoragePurchaseModal';

const EXPIRING_SOON_DAYS = 7;
const LOW_CREDIT_THRESHOLD = 50;

function isExpiringSoon(isoDate, days = EXPIRING_SOON_DAYS) {
  if (!isoDate) return false;
  const expiry = new Date(isoDate);
  if (Number.isNaN(expiry.getTime())) return false;
  const diffMs = expiry.getTime() - Date.now();
  const windowMs = days * 24 * 60 * 60 * 1000;
  return diffMs > 0 && diffMs <= windowMs;
}

function formatExpiryLabel(quota, key) {
  const label = quota?.[`${key}Label`];
  if (label) return String(label);
  const raw = quota?.[key];
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(getIpeUiLocale());
}

/**
 * 根据账号中心数据生成当前工作区通知列表（空间、IPE豆、赠送豆、店铺授权等）。
 * 文案一律带个人/团队前缀，与 resolveScopedQuota 数据轨一致。
 */
export function buildPersonalNotifications(accountCenter, workspaceMode) {
  if (!accountCenter) return [];

  const scope = normalizeWorkspaceMode(workspaceMode);
  const label = i18n.t(scope === 'team' ? 'teamShort' : 'personalShort');
  const quota = resolveScopedQuota(accountCenter, scope);
  const notifications = [];

  if (isUserStorageCapacityWarning(quota)) {
    notifications.push({
      id: `storage-low-${scope}`,
      type: 'storage-low',
      title: i18n.t('storageLowTitle', { scope: label }),
      message: resolveUserStorageCapacityWarningMessage(quota, scope),
      actionLabel: i18n.t('increaseStorage'),
      actionType: 'storage-purchase',
      createdAt: Date.now(),
    });
  }

  const creditBalance = Number(quota.creditBalance ?? quota.usageRemaining) || 0;
  if (quota.creditsEmpty || creditBalance <= 0) {
    notifications.push({
      id: `credits-empty-${scope}`,
      type: 'credits-empty',
      title: i18n.t('creditsEmptyTitle', { scope: label }),
      message: i18n.t('creditsEmptyMessage', { scope: label }),
      actionLabel: i18n.t('rechargeNow'),
      actionType: 'credits-purchase',
      createdAt: Date.now(),
    });
  } else if (creditBalance < LOW_CREDIT_THRESHOLD) {
    notifications.push({
      id: `credits-low-${scope}`,
      type: 'credits-low',
      title: i18n.t('creditsLowTitle', { scope: label }),
      message: i18n.t('creditsLowMessage', { scope: label, amount: formatCreditAmount(creditBalance) }),
      actionLabel: i18n.t('goRecharge'),
      actionType: 'credits-purchase',
      createdAt: Date.now(),
    });
  }

  const giftCredits = Number(quota.giftCredits) || 0;
  const membershipExpiry = quota.membershipExpiresAt;
  if (giftCredits > 0 && isExpiringSoon(membershipExpiry)) {
    const expiryLabel = formatExpiryLabel(quota, 'membershipExpiresAt');
    notifications.push({
      id: `gift-expiring-${scope}`,
      type: 'gift-expiring',
      title: i18n.t('giftExpiringTitle', { scope: label }),
      message: i18n.t('giftExpiringMessage', {
        scope: label,
        amount: formatCreditAmount(giftCredits),
        when: expiryLabel || i18n.t('soon'),
      }),
      actionLabel: i18n.t('renewMembership'),
      actionType: 'membership-purchase',
      createdAt: Date.now(),
    });
  }

  const rechargeCredits = Number(quota.rechargeCredits) || 0;
  const rechargeExpiry = quota.rechargeCreditExpiresAt;
  if (rechargeCredits > 0 && isExpiringSoon(rechargeExpiry)) {
    const expiryLabel = formatExpiryLabel(quota, 'rechargeCreditExpiresAt');
    notifications.push({
      id: `recharge-expiring-${scope}`,
      type: 'recharge-expiring',
      title: i18n.t('rechargeExpiringTitle', { scope: label }),
      message: i18n.t('rechargeExpiringMessage', {
        scope: label,
        amount: formatCreditAmount(rechargeCredits),
        when: expiryLabel || i18n.t('soon'),
      }),
      actionLabel: i18n.t('viewBalance'),
      actionType: 'account-center',
      createdAt: Date.now(),
    });
  }

  if (quota.shopAtLimit) {
    const connected = Number(quota.connectedShops) || 0;
    const limit = Number(quota.shopLimit) || 0;
    notifications.push({
      id: `shop-limit-${scope}`,
      type: 'shop-limit',
      title: i18n.t('shopLimitTitle', { scope: label }),
      message: i18n.t('shopLimitMessage', { scope: label, connected, limit }),
      actionLabel: i18n.t('expandShops'),
      actionType: 'shop-purchase',
      createdAt: Date.now(),
    });
  }

  return notifications;
}
